import { createTask, runAgent } from '@/api/agenticsearch'
import { searchAssessmentsByTitle } from '@/api/algolia'
import { nanoid } from 'nanoid'

export default {
  namespaced: true,
  state: {
    events: [],
    loading: false,
    error: null,
    eventSource: null,
    currentTaskId: null,
    searchCompleteCallbacks: [],
    latestSearch: {
      prompt: null,
      searchId: null,
      searchGrade: null,
      inputTokens: null,
      outputTokens: null,
      files: []
    },
  },
  mutations: {
    addEvent(state, event) {
      state.events.push(event)
    },
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    setEventSource(state, eventSource) {
      state.eventSource = eventSource
    },
    setCurrentTaskId(state, taskId) {
      state.currentTaskId = taskId
    },
    clearResults(state) {
      state.events = []
      state.loading = false
      state.error = null
      state.currentTaskId = null
    },
    clearSearch(state) {
      state.latestSearch = {
        prompt: null,
        searchId: null,
        searchGrade: null,
        inputTokens: null,
        outputTokens: null,
        files: [],
      }
    },
    setLatestSearchPrompt(state, prompt) {
      state.latestSearch.prompt = prompt
    },
    clearLatestSearchPrompt(state) {
      state.latestSearch.prompt = null
    },
    setLatestSearch(state, updates) {
      state.latestSearch = { ...state.latestSearch, ...updates }
    },
    setTokenCounts(state, { inputTokens, outputTokens }) {
      state.latestSearch.inputTokens = inputTokens
      state.latestSearch.outputTokens = outputTokens
    },
    addSearchCompleteCallback(state, callback) {
      state.searchCompleteCallbacks.push(callback)
    },
    removeSearchCompleteCallback(state, callback) {
      const index = state.searchCompleteCallbacks.indexOf(callback)
      if (index > -1) {
        state.searchCompleteCallbacks.splice(index, 1)
      }
    },
    clearSearchCompleteCallbacks(state) {
      state.searchCompleteCallbacks = []
    }
  },
  actions: {
    async search({ commit, dispatch, rootGetters, state }, { query, addlPrompt, files, filters, searchGrade }) {
      await dispatch('clearSearchAndResults')
      commit('setLoading', true)
      commit('setError', null)
      const searchId = nanoid()
      const updates = {
        prompt: query,
        searchId,
        searchGrade: searchGrade
      }
      commit('setLatestSearch', updates)
      if (addlPrompt) {
        query = `${query} ${addlPrompt}`
      }

      try {
        // Get user's state from user store, fallback to content_profile_id
        let userState = rootGetters['user/eventMetadata']?.ust || ''
        if (!userState) {
          userState = rootGetters['user/userContentProfileId'] || ''
        }
        const formData = buildSearchFormData(query, files, filters, userState)
        const taskResponse = await createTask(formData)
        const taskId = taskResponse.task_id
        commit('setCurrentTaskId', taskId)

        const eventSource = runAgent(
          taskId,
          getAgentEventCallback(commit, state),
          (error) => {
            commit('setError', error.message || 'An error occurred during search')
            commit('setLoading', false)
          },
          () => {
            commit('setLoading', false)
          }
        )

        commit('setEventSource', eventSource)
        return searchId
      } catch (error) {
        commit('setError', error.message || 'Failed to start search')
        commit('setLoading', false)
        throw error
      }
    },
    async searchAssessments({ dispatch, commit }, { query, files, filters, searchGrade }) {
      const assessmentPrompt = `(Assessments Only)

ADDITIONAL NOTES:
- If the query is short with minimal context, treat the request as a keyword search for assessments.
- When providing an assessment rationale, don't reference other assessments.

CONSTRAINTS:
- Don't include assessments about braille UNLESS the SEARCH REQUEST specifically mentions the student is blind / low vision.
`
      const onSearchComplete = () => dispatch('searchAssessmentSets')
      const searchId = await dispatch('search', { query, addlPrompt: assessmentPrompt, files, filters, searchGrade })
      commit('setLatestSearch', { files: files || [] })
      commit('addSearchCompleteCallback', onSearchComplete)
      return searchId
    },
    async searchAssessmentSets({ commit, getters }) {
      const assessmentResults = getters.assessmentResults
      const allAdditionalSets = []
      const seenUuids = new Set(assessmentResults.map(a => a.uuid))

      for (const assessment of assessmentResults) {
        if (!assessment.set_number) {
          continue
        }

        try {
          const allResults = await searchAssessmentsByTitle(assessment.title, assessment.grades)
          const matchingSets = allResults.filter(result => result.title === assessment.title)

          const additionalSets = matchingSets
            .filter(set => {
              if (seenUuids.has(set.uuid)) {
                return false
              }
              seenUuids.add(set.uuid)
              return true
            })
            .map(set => {
              const { content_alignments, domains, ...rest } = set
              return {
                ...rest,
                type: 'assessment',
                relevance_score: assessment.relevance_score,
                rationale: assessment.rationale
              }
            })
            .sort((a, b) => a.set_number - b.set_number)

          allAdditionalSets.push(...additionalSets)
        } catch (error) {
          console.error(`Failed to search for assessment sets for "${assessment.title}":`, error)
        }
      }

      for (const set of allAdditionalSets) {
        commit('addEvent', {
          type: 'content_generated',
          data: set,
          timestamp: new Date().toISOString()
        })
      }
    },
    clearResults({ commit, state }) {
      if (state.eventSource) {
        state.eventSource.close()
        commit('setEventSource', null)
      }
      commit('clearSearchCompleteCallbacks')
      commit('clearResults')
    },
    clearSearchAndResults({ commit, dispatch }) {
      dispatch('clearResults')
      commit('clearSearch')
    },
  },
  getters: {
    events(state) {
      return state.events
    },
    contentResults(state, getters) {
      if (!state.events || !Array.isArray(state.events)) {
        return []
      }
      return state.events
        .filter(e => e.type === 'content_generated')
        .map(event => event.data)
        .filter(item => item && item.relevance_score >= 7)
        .sort((a, b) => (b.relevance_score || 0) - (a.relevance_score || 0))
    },
    assessmentResults(state, getters) {
      return getters.contentResults
        .filter(event => event.type === 'assessment')
        .sort((a, b) => {
          const scoreDiff = (b.relevance_score || 0) - (a.relevance_score || 0)
          if (scoreDiff !== 0) return scoreDiff
          return (a.title || '').localeCompare(b.title || '')
        })
    },
    hasAssessmentResults(state, getters) {
      return getters.assessmentResults.length > 0
    },
    loading(state) {
      return state.loading
    },
    error(state) {
      return state.error
    },
    currentPhase(state) {
      const phaseEvents = state.events.filter(e => e.type === 'phase_started')
      if (phaseEvents.length === 0) return null
      const latestPhase = phaseEvents[phaseEvents.length - 1]
      return latestPhase.data?.phase
    },
    latestSearchPrompt(state) {
      return state.latestSearch.prompt
    },
    latestTokenUsage(state) {
      return {
        inputTokens: state.latestSearch.inputTokens,
        outputTokens: state.latestSearch.outputTokens,
      }
    },
    latestSearchId(state) {
      return state.latestSearch.searchId
    },
    latestSearchGrade(state) {
      return state.latestSearch.searchGrade
    },
    latestSearchFiles(state) {
      return state.latestSearch.files || []
    },
  },
}

function buildSearchFormData(query, files, filters, userState) {
  const formData = new FormData()

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files[]', file)
    })
  }

  const config = {
    prompt: query,
    model: 'gpt-4',
    temperature: 0.7,
  }
  formData.append('config', JSON.stringify(config))

  if (filters) {
    if (filters.studentName) {
      formData.append('studentName', filters.studentName)
    }
    if (filters.gradeLevels && filters.gradeLevels.length > 0) {
      formData.append('gradeLevel', filters.gradeLevels.join(','))
    }
    if (filters.subject) {
      formData.append('subject', filters.subject)
    }
    if (filters.category) {
      formData.append('category', filters.category)
    }
  }

  // Always include user's state if available
  if (userState) {
    formData.append('state', userState)
  }

  return formData
}

export function getAgentEventCallback(commit, state) {
  return (data) => {
    try {
      const eventWithTimestamp = {
        ...data,
        timestamp: new Date().toISOString(),
      }

      if (data.type === 'generation_complete' && data.data) {
        commit('setTokenCounts', {
          inputTokens: data.data.input_tokens,
          outputTokens: data.data.output_tokens
        })
      } else if (data.type === 'content_generated' || data.type === 'phase_started') {
        commit('addEvent', eventWithTimestamp)
      } else if (data.type === 'completed' || data.type === 'error') {
        commit('addEvent', eventWithTimestamp)
        commit('setLoading', false)
        const callbacks = [...state.searchCompleteCallbacks]
        commit('clearSearchCompleteCallbacks')
        Promise.allSettled(callbacks.map(callback => Promise.resolve().then(() => callback()))).then(results => {
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              console.error(`Search complete callback ${index} failed:`, result.reason)
            }
          })
        }).catch(error => {
          console.error('Error executing search complete callbacks:', error)
        })
      }
    } catch (error) {
      console.error('Error processing agent event:', error, 'Event data:', data)
      commit('setError', `Error processing event: ${error.message}`)
      commit('setLoading', false)
    }
  }
}
